/**
 * JobFlagsChips
 *
 * A small, reusable chip row for flagging a job (org × year) with any
 * combination of Marketing / Photography / Production / Financials.
 * Each flag is color-coded and can carry a short free-text note (edited
 * via a popover). The component owns its own network calls so both the
 * Organisation Details quick-view AND the Data Preview cell can drop it
 * in without ceremony.
 *
 * Props:
 *   - orgId    (required): organisation id
 *   - year     (required): job year (integer)
 *   - compact  (optional): render as ultra-compact chip strip (used in
 *                           the table cell). Defaults false.
 *   - flags    (optional): initial flag list ({flag_type, note}[]) to
 *                           avoid a network round-trip when the caller
 *                           already fetched them (e.g. by-year bulk load).
 *   - onChange (optional): (updatedFlags: [{flag_type, note}]) => void.
 *                           Called after a successful mutation so the
 *                           caller can update its local cache.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { authJsonFetch, authFetch } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { X } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Color palette shared with the picker. Kept in one place so the chip
// row in the top table AND the details editor stay in sync.
export const FLAG_TYPES = [
  { key: 'marketing',   label: 'Marketing',   short: 'M', color: 'amber',   bg: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700' },
  { key: 'photography', label: 'Photography', short: 'P', color: 'blue',    bg: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700' },
  { key: 'production',  label: 'Production',  short: 'R', color: 'violet',  bg: 'bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-900/40 dark:text-violet-100 dark:border-violet-700' },
  { key: 'financials',  label: 'Financials',  short: 'F', color: 'emerald', bg: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700' },
];

export const FLAG_TYPE_BY_KEY = Object.fromEntries(FLAG_TYPES.map(f => [f.key, f]));

export const JobFlagsChips = ({
  orgId,
  year,
  compact = false,
  flags: initialFlags = null,
  onChange,
}) => {
  const [flags, setFlags] = useState(Array.isArray(initialFlags) ? initialFlags : []);
  const [loading, setLoading] = useState(initialFlags === null);
  const [saving, setSaving] = useState('');       // flag_type currently being toggled
  const [editingNote, setEditingNote] = useState(null);   // flag_type whose note editor is open
  const [noteDraft, setNoteDraft] = useState('');

  // Sync when caller pushes fresh data (e.g. year switch).
  useEffect(() => {
    if (Array.isArray(initialFlags)) {
      setFlags(initialFlags);
      setLoading(false);
    }
  }, [initialFlags]);

  // Solo load only when caller didn't seed us. Bulk callers should pass
  // `flags` so we don't hit the API once per row.
  useEffect(() => {
    if (Array.isArray(initialFlags)) return;
    if (!orgId || !year) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await authFetch(`${API}/job-flags/for-org?org_id=${encodeURIComponent(orgId)}&year=${year}`);
        if (!res.ok) throw new Error('load-failed');
        const j = await res.json();
        if (!cancelled) setFlags(Array.isArray(j.flags) ? j.flags : []);
      } catch (e) {
        if (!cancelled) setFlags([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, year, initialFlags]);

  const flagByType = React.useMemo(() => {
    const m = {};
    for (const f of flags) m[f.flag_type] = f;
    return m;
  }, [flags]);

  const emitChange = useCallback((next) => {
    setFlags(next);
    onChange?.(next);
  }, [onChange]);

  const toggleFlag = useCallback(async (flagType) => {
    if (!orgId || !year) return;
    setSaving(flagType);
    try {
      const existing = flagByType[flagType];
      if (existing) {
        // Remove
        const res = await authJsonFetch(`${API}/job-flags`, {
          method: 'DELETE',
          body: JSON.stringify({ org_id: orgId, year, flag_type: flagType }),
        });
        if (!res.ok) throw new Error('delete failed');
        emitChange(flags.filter(f => f.flag_type !== flagType));
      } else {
        // Add (no note yet)
        const res = await authJsonFetch(`${API}/job-flags`, {
          method: 'PUT',
          body: JSON.stringify({ org_id: orgId, year, flag_type: flagType, note: '' }),
        });
        if (!res.ok) throw new Error('add failed');
        const j = await res.json();
        emitChange([...flags, j.flag]);
      }
    } catch (e) {
      toast.error(`Could not toggle ${flagType}`);
    } finally {
      setSaving('');
    }
  }, [orgId, year, flagByType, flags, emitChange]);

  const saveNote = useCallback(async (flagType, note) => {
    if (!orgId || !year) return;
    setSaving(flagType);
    try {
      const res = await authJsonFetch(`${API}/job-flags`, {
        method: 'PUT',
        body: JSON.stringify({ org_id: orgId, year, flag_type: flagType, note: note || '' }),
      });
      if (!res.ok) throw new Error('note save failed');
      const j = await res.json();
      const next = flags.filter(f => f.flag_type !== flagType).concat(j.flag);
      emitChange(next);
      toast.success('Note saved');
      setEditingNote(null);
    } catch (e) {
      toast.error('Could not save note');
    } finally {
      setSaving('');
    }
  }, [orgId, year, flags, emitChange]);

  if (loading) {
    return <div className="text-xs text-muted-foreground italic">Loading flags…</div>;
  }

  // COMPACT variant — used inside the Data View table cell. Renders only
  // the chips that are ON, plus a small "+" button to open the picker.
  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-1 min-h-[22px] hover:bg-muted/60 rounded px-1 py-0.5 -mx-1 -my-0.5"
            data-testid="job-flag-chips-compact"
            onClick={(e) => e.stopPropagation()}
            title="Click to edit flags"
          >
            {flags.length === 0 && (
              <span className="text-[10px] text-muted-foreground/70 italic px-1">+ flag</span>
            )}
            {flags.map(f => {
              const meta = FLAG_TYPE_BY_KEY[f.flag_type];
              if (!meta) return null;
              return (
                <span
                  key={f.flag_type}
                  className={cn(
                    'inline-flex items-center justify-center rounded-full border text-[10px] font-semibold leading-none w-5 h-5',
                    meta.bg,
                  )}
                  title={`${meta.label}${f.note ? ' · ' + f.note : ''}`}
                >
                  {meta.short}
                </span>
              );
            })}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-3"
          side="bottom"
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <FlagPickerBody
            flags={flags}
            flagByType={flagByType}
            saving={saving}
            toggleFlag={toggleFlag}
            editingNote={editingNote}
            setEditingNote={setEditingNote}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            saveNote={saveNote}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // FULL variant — used inside the Organisation Details quick-view.
  return (
    <div className="space-y-2" data-testid="job-flag-chips-full">
      <div className="flex flex-wrap items-center gap-1.5">
        {FLAG_TYPES.map(meta => {
          const active = !!flagByType[meta.key];
          return (
            <button
              key={meta.key}
              onClick={(e) => { e.stopPropagation(); toggleFlag(meta.key); }}
              disabled={saving === meta.key}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                active ? meta.bg : 'bg-muted/40 text-muted-foreground border-transparent hover:bg-muted',
                saving === meta.key && 'opacity-60',
              )}
              data-testid={`job-flag-toggle-${meta.key}`}
              title={active ? `${meta.label} flagged — click to remove` : `Flag as ${meta.label}`}
            >
              <span className={cn(
                'inline-flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-bold',
                active ? 'bg-white/70 dark:bg-black/30' : '',
              )}>{meta.short}</span>
              {meta.label}
              {active && flagByType[meta.key]?.note ? (
                <span className="text-[10px] opacity-70 italic truncate max-w-[100px]" title={flagByType[meta.key].note}>
                  · {flagByType[meta.key].note}
                </span>
              ) : null}
              {active && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setNoteDraft(flagByType[meta.key]?.note || '');
                    setEditingNote(meta.key);
                  }}
                  className="ml-0.5 text-[10px] underline decoration-dotted hover:opacity-80"
                  role="button"
                  data-testid={`job-flag-note-btn-${meta.key}`}
                >
                  {flagByType[meta.key]?.note ? 'edit' : '+ note'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Inline note editor — appears below the row when a chip's "+note" is clicked. */}
      {editingNote && (
        <div className="rounded-md border bg-muted/30 p-2 space-y-2" data-testid="job-flag-note-editor">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">
              {FLAG_TYPE_BY_KEY[editingNote]?.label} note
            </span>
            <button
              onClick={() => setEditingNote(null)}
              className="text-muted-foreground hover:text-foreground"
              title="Cancel"
              data-testid="job-flag-note-cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <Textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder={`Short note about the ${FLAG_TYPE_BY_KEY[editingNote]?.label.toLowerCase()} flag…`}
            rows={2}
            className="text-xs"
            data-testid="job-flag-note-input"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingNote(null)}
              className="h-7 text-xs"
            >Cancel</Button>
            <Button
              size="sm"
              onClick={() => saveNote(editingNote, noteDraft)}
              disabled={saving === editingNote}
              className="h-7 text-xs"
              data-testid="job-flag-note-save"
            >
              {saving === editingNote ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component: content of the compact popover picker.
const FlagPickerBody = ({
  flags,
  flagByType,
  saving,
  toggleFlag,
  editingNote,
  setEditingNote,
  noteDraft,
  setNoteDraft,
  saveNote,
}) => {
  return (
    <div className="space-y-2" data-testid="job-flag-picker">
      <div className="text-xs font-semibold mb-1">Flags</div>
      {FLAG_TYPES.map(meta => {
        const active = !!flagByType[meta.key];
        return (
          <div key={meta.key} className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); toggleFlag(meta.key); }}
                disabled={saving === meta.key}
                className={cn(
                  'flex-1 inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-medium text-left transition-colors',
                  active ? meta.bg : 'bg-muted/30 text-muted-foreground border-transparent hover:bg-muted',
                )}
                data-testid={`job-flag-picker-${meta.key}`}
              >
                <span className={cn(
                  'inline-flex items-center justify-center rounded-full w-5 h-5 text-[10px] font-bold flex-shrink-0',
                  active ? 'bg-white/70 dark:bg-black/30' : '',
                )}>{meta.short}</span>
                <span className="flex-1">{meta.label}</span>
                {active && (
                  <span className="text-[10px] opacity-70">on</span>
                )}
              </button>
              {active && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNoteDraft(flagByType[meta.key]?.note || '');
                    setEditingNote(meta.key === editingNote ? null : meta.key);
                  }}
                  className="text-[11px] px-1.5 py-1 rounded hover:bg-muted"
                  data-testid={`job-flag-picker-note-${meta.key}`}
                  title="Add / edit note"
                >
                  {flagByType[meta.key]?.note ? 'edit note' : '+ note'}
                </button>
              )}
            </div>
            {editingNote === meta.key && (
              <div className="space-y-1 pl-1">
                <Textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Short note…"
                  rows={2}
                  className="text-xs"
                />
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingNote(null)}
                    className="h-6 text-[11px] px-2"
                  >Cancel</Button>
                  <Button
                    size="sm"
                    onClick={() => saveNote(meta.key, noteDraft)}
                    disabled={saving === meta.key}
                    className="h-6 text-[11px] px-2"
                  >Save</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default JobFlagsChips;
